
import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import {
  Search,
  ShoppingCart,
  Heart,
  User,
  Menu,
  X,
  LogOut,
  Settings,
  Package,
  ChevronDown,
  ChevronRight,
  Home,
  ArrowRight,
} from "lucide-react"
import type { RootState } from "../../store/store"
import { logout } from "../../store/slices/authSlice"
import SearchBar from "../common/SearchBar"
import toast from "react-hot-toast"
const Logo = "/Logo.png";
import { collection, getDocs, query, where } from "firebase/firestore"
import { db } from "../../firebase/config"

interface DisplayCategory {
  name: string
  path: string
  slug: string
  subcategories?: DisplayCategory[]
  productCount?: number
}

interface Product {
  id: string
  name: string
  slug: string
  categories: string[]
  categoryNames?: string[]
  price: number
  finalPrice: number
  thumbnail: string
  isActive: boolean
  stock: number
  tags: string[]
  rating?: number
  numRatings?: number
}

const HARDCODED_CATEGORIES: DisplayCategory[] = [
  {
    name: "Sofa",
    slug: "sofas",
    path: "/categories/sofas",
    subcategories: [
      {
        name: "Wooden Sofa",
        slug: "wooden-sofa",
        path: "/categories/wooden-sofa",
      },
      {
        name: "Fabric Sofa",
        slug: "fabric-sofa",
        path: "/categories/fabric-sofa",
        subcategories: [
          {
            name: "L Shaped Sofa",
            slug: "l-shaped-sofa",
            path: "/categories/l-shaped-sofa",
          },
        ],
      },
    ],
  },
  {
    name: "Dining",
    slug: "dining",
    path: "/categories/dining",
  },
  {
    name: "Wardrobe",
    slug: "wardrobes",
    path: "/categories/wardrobes",
  },
  {
    name: "Chair",
    slug: "chairs",
    path: "/categories/chairs",
  },
  {
    name: "Tables",
    slug: "tables",
    path: "/categories/tables",
  },
  {
    name: "TV Units",
    slug: "tv-units",
    path: "/categories/tv-units",
  },
  {
    name: "Modular Kitchen",
    slug: "modular-kitchen",
    path: "/categories/modular-kitchen",
  },
  {
    name: "Safety Door",
    slug: "safety-door",
    path: "/categories/safety-door",
  },
  {
    name: "Mandir",
    slug: "mandir",
    path: "/categories/mandir",
  },
  {
    name: "Beds",
    slug: "beds",
    path: "/categories/beds",
  },
]

const Navbar: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { totalItems = 0 } = useSelector((state: RootState) => state.cart)
  const { items: wishlistItems = [] } = useSelector((state: RootState) => state.wishlist)

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [activeNestedDropdown, setActiveNestedDropdown] = useState<string | null>(null)
  const [mobileNestedLevel, setMobileNestedLevel] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [categoriesWithCount, setCategoriesWithCount] = useState<DisplayCategory[]>(HARDCODED_CATEGORIES)
  const [loading, setLoading] = useState(false)

  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const nestedDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const navRef = useRef<HTMLDivElement>(null)

  // Fetch products from Firebase
  const fetchProductsFromFirebase = async (): Promise<Product[]> => {
    try {
      setLoading(true)
      const productsRef = collection(db, "products")
      const q = query(productsRef, where("isActive", "==", true))
      const querySnapshot = await getDocs(q)

      const products: Product[] = []
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        products.push({
          id: doc.id,
          ...data,
          categories: Array.isArray(data.categories) ? data.categories : [data.category].filter(Boolean),
          categoryNames: data.categoryNames || [],
          tags: Array.isArray(data.tags) ? data.tags : [],
        } as Product)
      })

      return products
    } catch (error) {
      toast.error("Failed to load products")
      return []
    } finally {
      setLoading(false)
    }
  }

  // Calculate product counts for categories
  const calculateCategoryProductCounts = (products: Product[], categories: DisplayCategory[]): DisplayCategory[] => {
    return categories.map((category) => {
      const categoryProducts = products.filter((product) => {
        const hasInCategories = Array.isArray(product.categories) && product.categories.includes(category.slug)
        const hasInTags =
          Array.isArray(product.tags) &&
          (product.tags.includes(category.slug) ||
            product.tags.includes(category.name.toLowerCase()) ||
            product.tags.includes(category.name.toLowerCase().replace(/\s+/g, "-")))
        return hasInCategories || hasInTags
      })

      const updatedCategory = {
        ...category,
        productCount: categoryProducts.length,
      }

      if (category.subcategories) {
        updatedCategory.subcategories = calculateCategoryProductCounts(products, category.subcategories)
      }

      return updatedCategory
    })
  }

  useEffect(() => {
    const loadProducts = async () => {
      const fetchedProducts = await fetchProductsFromFirebase()
      setProducts(fetchedProducts)
      const updatedCategories = calculateCategoryProductCounts(fetchedProducts, HARDCODED_CATEGORIES)
      setCategoriesWithCount(updatedCategories)
    }
    loadProducts()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
        setActiveNestedDropdown(null)
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const checkCategoryHasProducts = (path: string): { exists: boolean; productCount: number } => {
    try {
      const pathParts = path.split("/").filter((part) => part)
      if (pathParts.length < 2) return { exists: false, productCount: 0 }

      const categorySlug = pathParts[pathParts.length - 1]
      const categoryExists = findCategoryBySlug(categoriesWithCount, categorySlug)

      if (!categoryExists) {
        return { exists: false, productCount: 0 }
      }

      const categoryProducts = products.filter((product) => {
        const hasInCategories = Array.isArray(product.categories) && product.categories.includes(categorySlug)
        const hasInTags =
          Array.isArray(product.tags) &&
          (product.tags.includes(categorySlug) ||
            product.tags.includes(categoryExists.name.toLowerCase()) ||
            product.tags.includes(categoryExists.name.toLowerCase().replace(/\s+/g, "-")))
        return hasInCategories || hasInTags
      })

      return {
        exists: true,
        productCount: categoryProducts.length,
      }
    } catch (error) {
      console.error("Error checking category:", error)
      return { exists: false, productCount: 0 }
    }
  }

  const findCategoryBySlug = (categories: DisplayCategory[], slug: string): DisplayCategory | null => {
    for (const category of categories) {
      if (category.slug === slug) {
        return category
      }
      if (category.subcategories) {
        const found = findCategoryBySlug(category.subcategories, slug)
        if (found) return found
      }
    }
    return null
  }

  const handleCategoryClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault()

    try {
      setError(null)
      const { exists, productCount } = checkCategoryHasProducts(path)

      if (exists && productCount > 0) {
        navigate(path)
        setIsMobileMenuOpen(false)
        setActiveDropdown(null)
        setActiveNestedDropdown(null)
        setMobileNestedLevel([])
      } else if (exists && productCount === 0) {
        const pathParts = path.split("/").filter((part) => part)
        const categoryName = pathParts[pathParts.length - 1]
          ?.replace(/-/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase())
        setError(`"${categoryName}" category has no products available yet. Please check back later.`)
        toast.error(`No products found in "${categoryName}" category`)
      } else {
        const pathParts = path.split("/").filter((part) => part)
        const categoryName = pathParts[pathParts.length - 1]
          ?.replace(/-/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase())
        setError(`"${categoryName}" category is not available yet. Please check back later.`)
        toast.error(`Category "${categoryName}" not found`)
      }

      setTimeout(() => setError(null), 5000)
    } catch (error) {
      console.error("Error handling category click:", error)
      setError("Something went wrong. Please try again.")
      toast.error("Failed to navigate to category")
    }
  }

  const handleLogout = () => {
    dispatch(logout())
    setIsUserMenuOpen(false)
    navigate("/")
    toast.success("Logged out successfully")
  }

  const handleDropdownEnter = (categorySlug: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    setActiveDropdown(categorySlug)
    setActiveNestedDropdown(null)
  }

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null)
      setActiveNestedDropdown(null)
    }, 300)
  }

  const handleNestedDropdownEnter = (subcategorySlug: string) => {
    if (nestedDropdownTimeoutRef.current) {
      clearTimeout(nestedDropdownTimeoutRef.current)
    }
    setActiveNestedDropdown(subcategorySlug)
  }

  const handleNestedDropdownLeave = () => {
    nestedDropdownTimeoutRef.current = setTimeout(() => {
      setActiveNestedDropdown(null)
    }, 300)
  }

  const navigateToMobileCategory = (categorySlug: string) => {
    const newLevel = [...mobileNestedLevel, categorySlug]
    setMobileNestedLevel(newLevel)
  }

  const navigateBackMobile = () => {
    const newLevel = [...mobileNestedLevel]
    newLevel.pop()
    setMobileNestedLevel(newLevel)
  }

  const resetMobileNavigation = () => {
    setMobileNestedLevel([])
  }

  const getCurrentMobileCategory = (): DisplayCategory[] => {
    let currentLevel = categoriesWithCount

    for (const slug of mobileNestedLevel) {
      const found = currentLevel.find((cat) => cat.slug === slug)
      if (found && found.subcategories) {
        currentLevel = found.subcategories
      } else {
        break
      }
    }

    return currentLevel
  }

  const renderDesktopDropdown = (category: DisplayCategory) => {
    if (!category.subcategories || category.subcategories.length === 0) return null

    const maxItemsToShow = 8
    const subcategoriesToShow = category.subcategories.slice(0, maxItemsToShow)
    const hasMoreItems = category.subcategories.length > maxItemsToShow

    return (
      <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl z-[10000] py-4 border border-gray-100 transform transition-all duration-200 ease-out">
        <div className="px-4 pb-3 mb-3 border-b border-gray-100">
          <div className="flex items-center text-xs text-gray-500 mb-2">
            <Home className="w-3 h-3 mr-1" />
            <span>Browse {category.name}</span>
            {category.productCount !== undefined && (
              <span className="ml-2 bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium">
                {category.productCount} products
              </span>
            )}
          </div>
          <button
            onClick={(e) => handleCategoryClick(e, category.path)}
            className="text-left font-semibold text-primary-700 hover:text-primary-800 text-sm py-2 px-3 rounded-lg hover:bg-primary-50 transition-all duration-200 w-full flex items-center justify-between group border border-transparent hover:border-primary-200"
          >
            <span>View All {category.name}</span>
            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-1" />
          </button>
        </div>

        <div className="px-2">
          <div className="space-y-1">
            {subcategoriesToShow.map((subcategory) => (
              <div key={subcategory.slug} className="relative group/item">
                {subcategory.subcategories && subcategory.subcategories.length > 0 ? (
                  <div
                    onMouseEnter={() => handleNestedDropdownEnter(subcategory.slug)}
                    onMouseLeave={handleNestedDropdownLeave}
                    className="relative"
                  >
                    <button
                      className="w-full text-left px-3 py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 flex items-center justify-between group transition-all duration-200 rounded-lg border border-transparent hover:border-primary-200 hover:shadow-sm"
                      onClick={(e) => handleCategoryClick(e, subcategory.path)}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{subcategory.name}</span>
                        {subcategory.productCount !== undefined && subcategory.productCount > 0 && (
                          <span className="text-xs text-gray-500">{subcategory.productCount} products</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-100 px-2 py-1 rounded-full">
                          {subcategory.subcategories.length}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
                      </div>
                    </button>

                    {activeNestedDropdown === subcategory.slug && (
                      <div className="absolute left-full top-0 ml-2 w-64 bg-white rounded-xl shadow-2xl z-[10001] py-3 border border-gray-100">
                        <div className="px-3 pb-2 mb-2 border-b border-gray-100">
                          <div className="flex items-center text-xs text-gray-500 mb-1">
                            <span className="font-medium">{subcategory.name}</span>
                          </div>
                          <button
                            onClick={(e) => handleCategoryClick(e, subcategory.path)}
                            className="text-left text-sm font-medium text-primary-700 hover:text-primary-800 py-1 px-2 rounded-lg hover:bg-primary-50 transition-colors w-full flex items-center justify-between group"
                          >
                            <span>View All</span>
                            <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                          </button>
                        </div>
                        <div className="px-2">
                          {subcategory.subcategories.slice(0, 6).map((nested) => (
                            <button
                              key={nested.slug}
                              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 rounded-lg transition-all duration-200 group border border-transparent hover:border-primary-200 mb-1"
                              onClick={(e) => handleCategoryClick(e, nested.path)}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{nested.name}</span>
                                {nested.productCount !== undefined && nested.productCount > 0 && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                    {nested.productCount}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                          {subcategory.subcategories.length > 6 && (
                            <button
                              onClick={(e) => handleCategoryClick(e, subcategory.path)}
                              className="block w-full text-left px-3 py-2 text-xs text-primary-600 hover:text-primary-700 font-medium rounded-lg hover:bg-primary-50 transition-colors"
                            >
                              View all {subcategory.subcategories.length} items →
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    className="block w-full text-left px-3 py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 rounded-lg transition-all duration-200 group border border-transparent hover:border-primary-200 hover:shadow-sm"
                    onClick={(e) => handleCategoryClick(e, subcategory.path)}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{subcategory.name}</span>
                      {subcategory.productCount !== undefined && subcategory.productCount > 0 && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {subcategory.productCount} products
                        </span>
                      )}
                    </div>
                  </button>
                )}
              </div>
            ))}
            {hasMoreItems && (
              <button
                onClick={(e) => handleCategoryClick(e, category.path)}
                className="block w-full text-left px-3 py-2 text-sm text-primary-600 hover:text-primary-700 font-medium rounded-lg hover:bg-primary-50 transition-colors border-t border-gray-100 mt-2 pt-3"
              >
                View all {category.subcategories.length} subcategories →
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const currentMobileCategories = getCurrentMobileCategory()
  const currentLevelName =
    mobileNestedLevel.length > 0
      ? mobileNestedLevel[mobileNestedLevel.length - 1].replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "Categories"

  return (
    <nav ref={navRef} className="bg-primary-900 shadow-lg sticky top-0 z-[9999]">
      {loading && (
        <div className="bg-primary-600 text-white text-center py-1 text-sm">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Loading categories...</span>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-primary-800 text-accent-400 py-2">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm">📞 +91 8010271912</span>
              <a 
  href="mailto:anmolfurniturebazaar@gmail.com" 
  className="hidden sm:inline text-xs sm:text-sm hover:text-white transition"
>
  ✉️ anmolfurniturebazaar@gmail.com
</a>

            </div>
            <div className="hidden md:flex items-center">
              <span className="text-xs lg:text-sm">Free Shipping on Orders Above ₹10,000</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img
              src={Logo || "/placeholder.svg"}
              alt="Anmol Furniture Bazar Logo"
              className="w-auto h-12 sm:h-14 lg:h-16 rounded-lg border-2 border-primary-600"
            />
          </Link>

          {/* Desktop Search */}
          <div className="hidden lg:block flex-1 max-w-2xl mx-8">
            <SearchBar />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <Link to="/wishlist" className="relative p-2 text-accent-400 hover:text-accent-300 transition-colors group">
              <Heart className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
              {wishlistItems?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {wishlistItems.length}
                </span>
              )}
            </Link>

            <Link to="/cart" className="relative p-2 text-accent-400 hover:text-accent-300 transition-colors group">
              <ShoppingCart className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {totalItems}
                </span>
              )}
            </Link>

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 text-accent-400 hover:text-accent-300 transition-colors group"
                >
                  <User className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
                  <span className="hidden xl:block font-medium truncate max-w-24">{user.name}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${isUserMenuOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-3 z-[10000] transform transition-all duration-200 ease-out">
                    <div className="px-4 py-3 border-b border-gray-100 mb-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-primary-50 transition-colors rounded-lg mx-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <Link
                      to="/orders"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-primary-50 transition-colors rounded-lg mx-2"
                    >
                      <Package className="w-4 h-4" />
                      <span>My Orders</span>
                    </Link>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-primary-50 transition-colors rounded-lg mx-2"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Admin Panel</span>
                      </Link>
                    )}
                    <hr className="my-2 mx-2" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 px-4 py-2.5 text-red-600 hover:bg-red-50 w-full text-left transition-colors rounded-lg mx-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Link to="/login" className="text-accent-400 hover:text-accent-300 transition-colors font-medium text-sm sm:text-base">
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-accent-400 text-primary-900 px-3 py-2 rounded-lg hover:bg-accent-300 transition-all duration-200 font-bold hover:scale-105 shadow-lg text-sm sm:text-base"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 text-accent-400 hover:text-accent-300 transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>
            <Link to="/cart" className="relative p-2 text-accent-400 hover:text-accent-300 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-xs">
                  {totalItems}
                </span>
              )}
            </Link>
            <button
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen)
                if (!isMobileMenuOpen) resetMobileNavigation()
              }}
              className="p-2 text-accent-400 hover:text-accent-300 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Categories Bar - Desktop Only */}
        <div className="hidden lg:flex items-center space-x-1 py-3 border-t border-primary-800 overflow-visible">
          {categoriesWithCount.slice(0, 10).map((category) => (
            <div
              key={category.slug}
              className="relative flex-shrink-0 static"
              onMouseEnter={() => handleDropdownEnter(category.slug)}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`flex items-center px-3 py-2.5 text-accent-400 hover:text-accent-200 hover:bg-primary-800 rounded-lg transition-all duration-200 font-medium focus:outline-none border border-transparent hover:border-primary-700 whitespace-nowrap ${
                  activeDropdown === category.slug ? "bg-primary-800 text-accent-200 border-primary-700" : ""
                }`}
                onClick={(e) => handleCategoryClick(e, category.path)}
              >
                <span className="text-sm">{category.name}</span>
                {category.productCount !== undefined && category.productCount > 0 && (
                  <span className="ml-2 bg-accent-400 text-primary-900 text-xs px-2 py-1 rounded-full font-bold">
                    {category.productCount}
                  </span>
                )}
                {category.subcategories && category.subcategories.length > 0 && (
                  <ChevronDown
                    className={`w-4 h-4 ml-2 transition-transform duration-200 ${
                      activeDropdown === category.slug ? "rotate-180" : ""
                    }`}
                  />
                )}
              </button>

              {activeDropdown === category.slug && renderDesktopDropdown(category)}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[104px] bg-white z-[9998] overflow-hidden">
          <div className="flex flex-col h-full">
            {/* Categories Navigation */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-4">
                {/* Navigation Header */}
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mb-4">
                  <div className="flex items-center space-x-2">
                    {mobileNestedLevel.length > 0 && (
                      <button
                        onClick={navigateBackMobile}
                        className="p-1 text-gray-500 hover:text-primary-600 transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 rotate-180" />
                      </button>
                    )}
                    <h3 className="font-semibold text-gray-900 flex items-center text-sm">
                      <Menu className="w-4 h-4 mr-2" />
                      {currentLevelName}
                    </h3>
                  </div>
                  {mobileNestedLevel.length > 0 && (
                    <button
                      onClick={resetMobileNavigation}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded"
                    >
                      Reset
                    </button>
                  )}
                </div>

                {/* Categories List */}
                <div className="space-y-2">
                  {currentMobileCategories.map((category) => (
                    <div key={category.slug} className="border rounded-lg bg-white shadow-sm">
                      <div className="flex items-center">
                        <button
                          onClick={(e) => handleCategoryClick(e, category.path)}
                          className="flex-1 text-left py-3 px-4 text-gray-700 hover:text-primary-600 font-medium transition-colors hover:bg-primary-50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm">{category.name}</span>
                            {category.productCount !== undefined && category.productCount > 0 && (
                              <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full font-medium ml-2">
                                {category.productCount}
                              </span>
                            )}
                          </div>
                        </button>
                        {category.subcategories && category.subcategories.length > 0 && (
                          <button
                            onClick={() => navigateToMobileCategory(category.slug)}
                            className="p-3 text-gray-400 hover:text-primary-600 transition-colors border-l border-gray-200 hover:bg-primary-50 flex-shrink-0"
                          >
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">{category.subcategories.length}</span>
                              <ChevronRight className="w-4 h-4" />
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* User Menu Footer */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50">
              <div className="p-4">
                {user ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 px-3 py-2 bg-white rounded-lg">
                      <User className="w-6 h-6 text-primary-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate text-sm">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link
                        to="/profile"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center space-x-2 py-2 px-3 text-gray-600 hover:bg-white rounded-lg transition-colors text-sm"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center space-x-2 py-2 px-3 text-gray-600 hover:bg-white rounded-lg transition-colors text-sm"
                      >
                        <Package className="w-4 h-4" />
                        <span>Orders</span>
                      </Link>
                      <Link
                        to="/wishlist"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center space-x-2 py-2 px-3 text-gray-600 hover:bg-white rounded-lg transition-colors text-sm"
                      >
                        <Heart className="w-4 h-4" />
                        <span>Wishlist ({wishlistItems?.length || 0})</span>
                      </Link>
                      {user.role === "admin" && (
                        <Link
                          to="/admin"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center space-x-2 py-2 px-3 text-gray-600 hover:bg-white rounded-lg transition-colors text-sm"
                        >
                          <Settings className="w-4 h-4" />
                          <span>Admin</span>
                        </Link>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsMobileMenuOpen(false)
                      }}
                      className="flex items-center justify-center space-x-2 py-2 px-3 text-red-600 hover:bg-white w-full text-left rounded-lg transition-colors text-sm font-medium"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-2 px-3 text-gray-600 hover:bg-white rounded-lg transition-colors font-medium text-center"
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-2 px-3 bg-primary-600 text-white text-center rounded-lg hover:bg-primary-700 transition-colors font-bold"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] lg:hidden">
          <div className="bg-white m-4 mt-20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Search Products</h3>
              <button
                onClick={() => setIsSearchOpen(false)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <SearchBar onSearch={() => setIsSearchOpen(false)} />
          </div>
        </div>
      )}

      {/* Error Popup */}
      {error && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-xl shadow-2xl z-[10002] max-w-sm mx-auto border border-red-400 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center mt-0.5">
                <X className="w-3 h-3" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">Category Not Available</p>
                <p className="text-xs opacity-90 mt-1 break-words">{error}</p>
              </div>
            </div>
            <button
              className="ml-2 text-white hover:text-gray-200 font-bold text-lg leading-none hover:bg-white hover:bg-opacity-20 rounded-full w-5 h-5 flex items-center justify-center transition-colors flex-shrink-0"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
